import React from 'react';
import { FilterIcon, RefreshCcw, Loader2 } from 'lucide-react';
import AddJobsiteButton from './AddJobsiteButton';

interface JobsiteHeaderProps {
  viewMode: 'map' | 'list';
  searchTerm: string;
  isOnline: boolean;
  hasOfflineData: boolean;
  isSyncing: boolean;
  getActiveFiltersCount: () => number;
  handleSearch: (value: string) => void;
  handleMapViewClick: () => void;
  handleListViewClick: () => void;
  handleSyncClick: () => void;
  setShowFilterOverlay: (show: boolean) => void;
  handleAddJobsite: () => void;
  hasAddAccess: boolean;
}

const JobsiteHeader: React.FC<JobsiteHeaderProps> = ({
  viewMode,
  searchTerm,
  isOnline,
  hasOfflineData,
  isSyncing,
  getActiveFiltersCount,
  handleSearch,
  handleMapViewClick,
  handleListViewClick,
  handleSyncClick,
  setShowFilterOverlay,
  handleAddJobsite,
  hasAddAccess,
}) => {
  return (
    <div className="bg-white rounded-[12px] shadow overflow-hidden p-3 sm:p-4 mb-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className="flex border border-[#E8E8E8] items-center bg-gray-100 rounded-lg p-1 w-full sm:w-fit">
            <button
              className={`flex-1 px-3 py-2 text-[16px] font-medium rounded-md transition h-[35px] whitespace-nowrap ${
                viewMode === 'list' ? 'bg-white shadow text-black' : 'text-[#55575B]'
              }`}
              onClick={handleListViewClick}
              id="list-view-button"
            >
              Table View
            </button>
            <button
              className={`flex-1 px-3 py-2 text-[16px] font-medium rounded-md transition h-[35px] whitespace-nowrap  ${
                viewMode === 'map' ? 'bg-white shadow text-black' : 'text-[#55575B]'
              }`}
              onClick={handleMapViewClick}
              id="map-view-button"
            >
              Map View
            </button>
          </div>

          {isOnline && (
            <form className="relative flex-grow sm:flex-grow-0" onSubmit={(e) => e.preventDefault()}>
              <input
                type="text"
                placeholder="Search by Jobsite Name and Status"
                className="w-full sm:w-80 pl-10 pr-9 py-2 border border-border rounded-[8px] text-[16px] focus:outline-none"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                id="jobsite-search-input"
              />
              <svg
                className="w-5 h-5 text-text-secondary absolute left-3 top-2.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchTerm && (
                <button
                  className="absolute right-3 top-3 text-text-secondary hover:text-text-primary"
                  onClick={() => handleSearch('')}
                  type="button"
                  id="clear-search-button"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </form>
          )}
        </div>
        <div className="hidden sm:flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto justify-end">
          {isOnline && hasOfflineData && (
            <button
              onClick={handleSyncClick}
              disabled={!isOnline || isSyncing}
              className={`flex items-center justify-center rounded-full p-2 transition-colors ${
                !isOnline ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-200'
              }`}
              title={isOnline ? 'Sync data' : 'Cannot sync while offline'}
              aria-label="Sync data"
            >
              {isSyncing ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <RefreshCcw size={20} className={isOnline ? 'text-[#A42823]' : 'text-gray-400'} />
              )}
            </button>
          )}
          {isOnline && (
            <button
              onClick={() => setShowFilterOverlay(true)}
              className={`relative btn-secondary flex items-center justify-center text-[24px] sm:text-[16px] rounded-full sm:text-body w-fit sm:w-auto sm:rounded-[8px] ${
                getActiveFiltersCount() > 0 ? 'bg-[#A42823] text-white hover:bg-[#8a2320]' : ''
              }`}
              id="filter-button"
            >
              <span className="hidden sm:inline mr-1">Filter</span>
              <FilterIcon className="w-4 h-4" />
              {getActiveFiltersCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#E6D69B] text-[#000000] text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          )}
          <AddJobsiteButton onClick={handleAddJobsite} hasAccess={hasAddAccess} />
        </div>
      </div>
    </div>
  );
};

export default JobsiteHeader; 